import React, { useState } from "react";
import { compose } from "redux";
import { withStyles } from "components/common";
import { Chart } from "react-charts";
import { fill } from "tailwindcss/defaultTheme";
import { makeBalancesDayAmountsEqualForGraph } from "./utils";
const _ = require('lodash');

const moment = require("moment");

const currOrAvailBal = (bal) => {
  return bal.current_balance === 0 ? 0 : bal.current_balance || bal.available_balance;
}

const CustomTooltip = ({ datum }) => {
  const series_labels =
    datum && datum.group
      ? datum.group.map(m => {
        return (
          <div key={m.seriesLabel} className={`${datum.seriesIndex === m.seriesIndex ? "text-yellow-400" : ""}`}>
            <span className="font-bold">${m.secondary}</span>
            {` - ${m.originalSeries.acct_name}`}
          </div>
        );
      })
      : [];
  return datum ? (
    <div
      style={{
        color: "white",
        pointerEvents: "none"
      }}
    >
      <h3
        style={{
          display: "block",
          textAlign: "center"
        }}
      >
        {datum.originalDatum.xlabel}
      </h3>
      <div>
        {series_labels}
      </div>
    </div>
  ) : null;
};

const AssetReportTable = ({ report, currentAccounts, loading, transactionReport = {} }) => {

  // in other section account_id is finicity_accounts.id to identify payment_methods
  // for this graph will need to set account_id = finicity_accounts.account_id to match transaction report id
  const modifiedCurrentAccounts = _.cloneDeep(currentAccounts);
  modifiedCurrentAccounts.forEach(ca => {
    if (ca.finicity_account_id){
      ca.account_id = parseInt(ca.finicity_account_id);
    }
  });

  const [{ primaryCursorValue, secondaryCursorValue }, setState] = useState({
    primaryCursorValue: null,
    secondaryCursorValue: null
  });

  const balances = [];

  const { report: r } = report || {};
  const { items } = r || {};
  const { institutions } = transactionReport || {};

  // pull historical balances from asset report
  if (items && items.length > 0) {
    items.forEach(i => {
      (i.accounts || {}).forEach(a => {
        const hist_bal = a.historical_balances
          .sort((b1, b2) => moment(b1.date) - moment(b2.date))
          .map((b,ii) => {
            return {
              x: ii,
              y: b.current,
              xlabel: moment(b.date, "YYYY-MM-DD").format("MM-DD-YYYY"),
              timeOfBalance: moment(b.date, "YYYY-MM-DD").toDate()
            };
          });
        balances.push({
          label: a.account_id,
          acct_name: `${a.name} (${a.type})`,
          data: hist_bal
        });
      })
    });
  };

  // pull historical balances from transaction report
  if (institutions && institutions.length > 0) {
    institutions.forEach(i => {
      (i.accounts || []).forEach(a => {
        const hist_bal = (a.generatedHistoricalBalances || [])
          .sort((b1, b2) => moment(b1.date) - moment(b2.date))
          .slice(Math.max(0, a.generatedHistoricalBalances.length - 365))
          .map((b, ii) => {
            return {
              x: ii,
              y: b.current,
              xlabel: moment(b.date, "YYYY-MM-DD").format("MM-DD-YYYY"),
              timeOfBalance: moment(b.date, "YYYY-MM-DD").toDate()
            };
          });
        balances.push({
          label: a.id,
          acct_name: `${a.name} (${a.type})`,
          data: hist_bal
        });
      })
    });
  };

  // add our historical balances after application approval
  const max_len = Math.max(...balances.map(b => b.data.length));
  modifiedCurrentAccounts.forEach(acct => {
    acct.balances.sort((a,b) => parseInt(a.created_at) - parseInt(b.created_at));
    const filteredAccounts = balances.filter(b => b.label === acct.account_id);
    if (filteredAccounts.length > 0) {
      const numBalances = filteredAccounts[0].data.length;
      (balances.filter(b => b.label === acct.account_id)[0]).data.push(...acct.balances.map((bb, ii) => {
        return {
          x: numBalances + ii,
          y: currOrAvailBal(bb),
          xlabel: moment(parseInt(bb.created_at)).format("MM-DD-YYYY"),
          timeOfBalance: bb.created_at
        };
      }));
      // now add newly linked bank accounts that were not included in the asset report
      if (!(balances.map(b => b.label).includes(acct.account_id))) {
        balances.push({
          label: acct.account_id,
          acct_name: `${acct.name} (${acct.type})`,
          data: acct.balances.map((bb, ii) => {
            return {
              x: max_len + ii,
              y: currOrAvailBal(bb),
              xlabel: moment(parseInt(bb.created_at)).format("MM-DD-YYYY"),
              timeOfBalance: bb.created_at
            };
          })
        });
      }
    } else {
      balances.push({
        label: acct.account_id,
        acct_name: `${acct.name} (${acct.type})`,
        data: acct.balances.map((bb, ii) => {
          return {
            x: max_len + ii,
            y: currOrAvailBal(bb),
            xlabel: moment(parseInt(bb.created_at)).format("MM-DD-YYYY"),
            timeOfBalance: bb.created_at
          };
        })
      });
    }
  });



  const axes = React.useMemo(
    () => [
      {
        primary: true,
        type: "linear",
        position: "bottom",
      },
      {
        type: "linear",
        position: "left"
      }
    ],
    []
  )


  const series = React.useMemo(
    () => ({
      showPoints: false
    }),
    []
  );

  const primaryCursor = React.useMemo(
    () => ({
      value: primaryCursorValue
    }),
    [primaryCursorValue]
  );
  const secondaryCursor = React.useMemo(
    () => ({
      value: secondaryCursorValue
    }),
    [secondaryCursorValue]
  );
  const onFocus = React.useCallback(datum => {
    setState({
      primaryCursorValue: datum && datum.originalDatum ? datum.originalDatum.xlabel : null,
      secondaryCursorValue: datum ? datum.secondary : null
    });
  }, []);

  const tooltip = React.useMemo(
    () => ({
      render: ({ datum, primaryAxis, getStyle }) => {
        return <CustomTooltip {...{ getStyle, primaryAxis, datum }} />;
      }
    }),
    []
  );

  // calculate total balances across all currently linked bank accounts
  const currentBalances = [];

  const mCAForCalculatingCurrentBalances  = modifiedCurrentAccounts.filter(acct => acct.type !== "creditCard");
  
   mCAForCalculatingCurrentBalances.forEach(acct => {
    const latestBalance = acct.balances.filter(
      b => moment(parseInt(b.created_at)) > moment().subtract(3, "days")
    );

    latestBalance.sort((a,b) => b.created_at - a.created_at)
    if (latestBalance.length > 0) {
      currentBalances.push(latestBalance[0])
    }
  });

  const s = currentBalances.reduce((total, a) => total + a.current_balance, 0);

  const lastBalances = balances.filter(
    acct => acct.data.length > 0 &&
      moment(parseInt(acct.data[acct.data.length - 1].timeOfBalance)) >
      moment().subtract(3, "days")
  );

  const avg = lastBalances.map(
    b =>
      b.data
        .slice(b.data.length - 7, b.data.length)
        .reduce((tb, bb) => tb + bb.y, 0) / 7
    )
    .reduce((tbb, bbb) => tbb + bbb, 0);

 

  const finalBalances = makeBalancesDayAmountsEqualForGraph(balances);
  const chartData = React.useMemo(() => finalBalances, [])

  return (
    <div className="mt-5">
      <h1 className="text-2xl font-title mb-2 font-light">
        Bank Balances
      </h1>
      <div>
        Current total Bank Balance: <span className="font-bold">${s.toFixed(2)}</span> <span className="text-gray-500">(across {currentBalances.length} bank accounts)</span>
      </div>
      <div>
        Avg from last 7 days: <span className="font-bold">${avg.toFixed(2)}</span> <span className="text-gray-500">(across {currentBalances.length} bank accounts)</span>
      </div>
      <div className="my-6">
        {loading ? (
          <div>Loading...</div>
        ) : balances.length ? (
          <div
            style={{
              width: "100%",
              height: "300px"
            }}
          >
            <Chart
              data={chartData}
              series={series}
              axes={axes}
              tooltip={tooltip}
              primaryCursor={primaryCursor}
              secondaryCursor={secondaryCursor}
            />
          </div>
        ) : (
          <div>No report is available</div>
        )}
      </div>
    </div>
  );
};

export default compose(withStyles)(AssetReportTable);
